import React, { memo } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { Feather } from "@expo/vector-icons";
import { cn } from "@/lib/utils";

export interface ErrorStateProps {
  /**
   * Bold heading (translation key).
   * Defaults to `errorState.title` — "Something went wrong".
   */
  title?: TranslationKeys;
  /**
   * Supporting description below the heading (translation key).
   * Defaults to `errorState.description`.
   */
  description?: TranslationKeys;
  /** Called when the user presses the retry button. Omit to hide the button. */
  onRetry?: () => void;
  /**
   * Label for the retry button (translation key).
   * Defaults to `errorState.retry` — "Try again".
   */
  retryLabel?: TranslationKeys;
  /**
   * Raw description string to render instead of the translated `description` key.
   * Use for dynamic server-provided messages (e.g. rejection reasons).
   */
  descriptionText?: string;
  /**
   * Custom icon node rendered above the heading.
   * When omitted a default warning symbol is shown.
   */
  icon?: React.ReactNode;
  /** Forwarded to the root View for testing. */
  testID?: string;
  className?: string;
  onSecondaryAction?: () => void;
  secondaryActionLabel?: TranslationKeys;
}

/**
 * Full-width error state — shown when a screen or section fails to load.
 *
 * All text props are optional and fall back to the `errorState.*` i18n keys so
 * the component is usable with zero props:
 *
 * @example
 * // Zero-prop defaults
 * <ErrorState onRetry={refetch} />
 *
 * @example
 * // Custom copy
 * <ErrorState
 *   title="review.loadError.title"
 *   description="review.loadError.description"
 *   onRetry={refetch}
 * />
 */
export const ErrorState = memo(function ErrorState({
  title = "errorState.title",
  description = "errorState.description",
  onRetry,
  retryLabel = "errorState.retry",
  descriptionText,
  icon,
  testID,
  className,
  onSecondaryAction,
  secondaryActionLabel,
}: ErrorStateProps) {
  const { t } = useI18n();

  return (
    <View
      testID={testID}
      className={cn("flex-1 items-center justify-center px-6 py-12 gap-4", className)}
      accessibilityRole="alert"
    >
      {/* ── Icon ─────────────────────────────────────────────────────────── */}
      {icon != null ? (
        icon
      ) : (
        <View className="bg-error-light rounded-full p-5">
          <Icon as={Feather} name="x" size={40} className="text-error" />
        </View>
      )}

      {/* ── Text ─────────────────────────────────────────────────────────── */}
      <View className="items-center gap-2">
        <AppText weight="bold" className="text-lg text-dark-text-50 text-center">
          {t(title)}
        </AppText>
        <AppText
          weight="regular"
          className="text-sm text-supporting-text text-center leading-5 max-w-sm"
        >
          {descriptionText ?? t(description)}
        </AppText>
      </View>

      {(onSecondaryAction || onRetry) && (
        <View className="flex-row gap-4">
          {onSecondaryAction != null && secondaryActionLabel != null && (
            <Button
              label={secondaryActionLabel}
              variant="secondary"
              onPress={onSecondaryAction}
              testID={testID != null ? `${testID}-secondary` : undefined}
            />
          )}

          {/* ── Retry button ─────────────────────────────────────────────────── */}
          {onRetry != null && (
            <Button
              label={retryLabel}
              variant="primary"
              onPress={onRetry}
              testID={testID != null ? `${testID}-retry` : undefined}
            />
          )}
        </View>
      )}
    </View>
  );
});
